import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  getPublishedStatus,
  getWorkingProjectUuid,
  selectProjectName,
  selectWorkingProjectMagicLinkRegistrationsPasscodeListId,
  selectWorkingProjectRegistrationSettings,
} from '@/store/slices/project/selectors';
import { WorkingPasscodeList } from '@/types/passcode-list.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { EProjectStatus } from '@/types/projects.types';
import {
  GenerateMagicLinksForEvent,
  GenerateMagicLinksForList,
  SendUnsentMagicLinkRegistrationEmails,
  UploadMagicLinkPasscodeListMembers,
  UploadPasscodeListMembers,
} from '@/connection/audiences/passcode-lists';
import { PASSCODE_LIST_TYPE } from '@/types/registration.types';
import { getFetchErrorMessage } from '@/connection/helpers';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { selectPasscodeList, selectPasscodeLists } from '@/store/slices/passcode-lists/selectors';
import { EToastTypes } from '@ui/toast/toast.types';
import { setPasscodeListMemberCountSync } from '@/store/slices/passcode-lists';

export const useSaveRegistrantList = () => {
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const channel = useAppSelector(getActiveChannel);
  const publishedStatus = useAppSelector(getPublishedStatus);
  const projectName = useAppSelector(selectProjectName);
  const magicLinkRegistrationsPasscodeListId = useAppSelector(selectWorkingProjectMagicLinkRegistrationsPasscodeListId);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const passcodeLists = useAppSelector(selectPasscodeLists);
  const passcodeList = useAppSelector(selectPasscodeList);

  const dispatch = useAppDispatch();

  const [saving, setSaving] = useState(false);

  const isPublished = publishedStatus?.status === EProjectStatus.PUBLIC;

  const showSaveErrorToast = (msg = 'There was a problem saving the list.') => {
    toastTrigger(msg, {
      type: EToastTypes.error,
      autoClose: 10000,
    });
  };

  const showSuccessToast = (message: string) => {
    toastTrigger(message, {
      type: EToastTypes.success,
      autoClose: 10000,
    });
  };

  const showPublishRequiredError = () => {
    toastTrigger('Please Publish Project First', {
      description: `You need to publish ${projectName} first before you can attach a magic links list.`,
      type: EToastTypes.error,
      autoClose: 10000,
    });
  };

  const showDuplicateNameError = (name: string) => {
    toastTrigger('Duplicate Audience List Name', {
      description: `"${name}" is already in use. Please enter a unique name.`,
      type: EToastTypes.error,
      autoClose: 10000,
    });
  };

  const showEmailSendError = () => {
    toastTrigger(
      'We were unable to send some or all magic link emails. Please re-trigger the email by clicking "send unsent invites" button.',
      {
        type: EToastTypes.error,
        autoClose: 10000,
      }
    );
  };

  const validateAndPrepareList = (workingPasscodeList: WorkingPasscodeList) => {
    if (!channel || !projectUuid) {
      return null;
    }

    setSaving(true);

    const listToCreate = {
      ...workingPasscodeList,
      name: workingPasscodeList.name.trim(),
    };

    const lowerCaseName = listToCreate.name.toLowerCase();
    const isDuplicate = passcodeLists.some((list) => {
      return lowerCaseName === list.name?.toLowerCase() && !magicLinkRegistrationsPasscodeListId;
    });

    if (isDuplicate) {
      showDuplicateNameError(listToCreate.name);
      setSaving(false);
      return null;
    }

    return listToCreate;
  };

  const saveEmailsOnlyList = async (workingPasscodeList: WorkingPasscodeList) => {
    const listToCreate = validateAndPrepareList(workingPasscodeList);
    if (!listToCreate) return;

    try {
      if (!channel || !passcodeList?.passcode_list) {
        setSaving(false);
        return;
      }

      const response = await UploadPasscodeListMembers(
        channel,
        listToCreate.fileName || '',
        passcodeList?.passcode_list,
        listToCreate.type,
        listToCreate.url,
        listToCreate.manually_added_users
      );

      if (response.newCount) {
        dispatch(setPasscodeListMemberCountSync(response.newCount));
      }

      const message = response.message || 'List saved successfully.';
      showSuccessToast(message);

      setSaving(false);
    } catch (error) {
      console.error(error);
      showSaveErrorToast();
      setSaving(false);
    }
  };

  const savePasscodesOnlyList = async (workingPasscodeList: WorkingPasscodeList) => {
    const listToCreate = validateAndPrepareList(workingPasscodeList);
    if (!listToCreate) return;

    try {
      if (!channel || !passcodeList?.passcode_list) {
        setSaving(false);
        return;
      }

      const response = await UploadPasscodeListMembers(
        channel,
        listToCreate.url || '',
        passcodeList?.passcode_list,
        listToCreate.type,
        listToCreate.url,
        listToCreate.manually_added_users
      );

      if (!response || !response.passcode_list || !response.success) {
        showSaveErrorToast();
        setSaving(false);
        return;
      }

      const message = response.message || 'List saved successfully.';
      showSuccessToast(message);

      setSaving(false);
    } catch (error) {
      console.error(error);
      showSaveErrorToast();
      setSaving(false);
    }
  };

  const saveEmailsAndPasscodesList = async (workingPasscodeList: WorkingPasscodeList) => {
    const listToCreate = validateAndPrepareList(workingPasscodeList);
    if (!listToCreate) return;

    try {
      if (!channel || !passcodeList?.passcode_list) {
        setSaving(false);
        return;
      }
      const response = await UploadPasscodeListMembers(
        channel,
        listToCreate.url || '',
        passcodeList?.passcode_list,
        listToCreate.type,
        listToCreate.url,
        listToCreate.manually_added_users
      );

      if (!response || !response.passcode_list || !response.success) {
        showSaveErrorToast();
        setSaving(false);
        return;
      }

      const message = response.message || 'List saved successfully.';
      showSuccessToast(message);

      setSaving(false);
    } catch (error) {
      console.error(error);
      showSaveErrorToast();
      setSaving(false);
    }
  };

  const saveMagicLinksList = async (workingPasscodeList: WorkingPasscodeList) => {
    if (!isPublished) {
      showPublishRequiredError();
      return;
    }

    const listToCreate = validateAndPrepareList(workingPasscodeList);
    if (!listToCreate) return;

    try {
      if (!channel || !projectUuid || !magicLinkRegistrationsPasscodeListId) {
        setSaving(false);
        return;
      }

      const response = await UploadMagicLinkPasscodeListMembers(
        channel,
        listToCreate.name,
        magicLinkRegistrationsPasscodeListId,
        listToCreate.type,
        listToCreate.url,
        listToCreate.manually_added_users,
        projectUuid
      );

      if (!response || !response.passcode_list || !response.success) {
        showSaveErrorToast();
        setSaving(false);
        return;
      }

      const message = response.message || 'List saved successfully.';

      if (response.passcode_list && response.type && channel) {
        try {
          await GenerateMagicLinksForList(channel, response.passcode_list);
        } catch (error) {
          console.error(error);
          showSaveErrorToast('Error generating magic links.');
          setSaving(false);
          return;
        }
      }

      showSuccessToast(message);

      setSaving(false);
    } catch (error) {
      console.error(error);
      showSaveErrorToast();
      setSaving(false);
    }
  };

  const saveMagicLinksRegistrationList = async (workingPasscodeList: WorkingPasscodeList) => {
    if (!isPublished) {
      showPublishRequiredError();
      return;
    }

    const listToCreate = validateAndPrepareList(workingPasscodeList);
    if (!listToCreate) return;

    try {
      if (!channel || !projectUuid) {
        setSaving(false);
        return;
      }

      const response = await UploadMagicLinkPasscodeListMembers(
        channel,
        listToCreate.name,
        magicLinkRegistrationsPasscodeListId,
        listToCreate.type,
        listToCreate.url,
        listToCreate.manually_added_users,
        projectUuid
      );

      if (!response || !response.passcode_list || !response.success) {
        showSaveErrorToast();
        setSaving(false);
        return;
      }

      let message = response.message || 'List saved successfully.';

      if (
        response.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION &&
        !magicLinkRegistrationsPasscodeListId &&
        response.passcode_list &&
        response?.passcode_list > 0
      ) {
        if (registrationSettings) {
          const _updatedSettings = {
            ...registrationSettings,
            magic_links_registration_passcode_list: response.passcode_list,
          };
          dispatch(saveWorkingProjectByFields({ registration_settings: _updatedSettings }));
        }
      }

      if (response.passcode_list && response.type && channel && projectUuid) {
        try {
          await GenerateMagicLinksForEvent(channel, response.passcode_list, projectUuid, response.type);
        } catch (error) {
          console.error(error);
          showSaveErrorToast('Error generating magic links.');
          setSaving(false);
          return;
        }
      }

      if (projectUuid && response.passcode_list) {
        try {
          const emailResponse = await SendUnsentMagicLinkRegistrationEmails(projectUuid, response.passcode_list);
          if (emailResponse.success) {
            message += ` ${emailResponse.message || 'Registration emails sent successfully.'}`;
          } else {
            showEmailSendError();
          }
        } catch (error) {
          showSaveErrorToast(getFetchErrorMessage(error, 'Error sending registration emails.'));
          console.error(error);
        }
      }

      showSuccessToast(message);

      setSaving(false);
    } catch (error) {
      console.error(error);
      showSaveErrorToast();
      setSaving(false);
    }
  };

  return {
    saveEmailsOnlyList,
    savePasscodesOnlyList,
    saveEmailsAndPasscodesList,
    saveMagicLinksList,
    saveMagicLinksRegistrationList,
    saving,
  };
};
